<script lang="ts" setup>
  import {
    ANCHOR_PATTERN,
    anchorsMode,
    isAnchorRendered,
  } from '@/featureBadges';
  import { useEnvironmentsStore } from '@/stores/environments';

  const DISCOVERABLE =
    'button[data-testid], a[data-testid], h1[data-testid], h2[data-testid], h3[data-testid], section[data-testid]';

  const environmentsStore = useEnvironmentsStore();
  const route = useRoute();

  watch(
    () => 'featureAnchors' in (route?.query ?? {}),
    (on) => {
      anchorsMode.value = on;
    },
    { immediate: true },
  );

  const hostSelector = (anchor: string) =>
    `[data-testid="${anchor}"]:not([data-badge-anchor])`;

  const domVersion = ref(0);
  let observer: MutationObserver | undefined;

  onMounted(() => {
    observer = new MutationObserver(() => {
      domVersion.value += 1;
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });

  onBeforeUnmount(() => observer?.disconnect());

  const hasRenderedAnchor = (element: Element) =>
    [...element.querySelectorAll('[data-badge-anchor]')].some((child) =>
      isAnchorRendered((child as HTMLElement).dataset.badgeAnchor ?? ''),
    );

  const badged = computed(() => {
    void domVersion.value;

    return Object.keys(environmentsStore.FEATURE_BADGES ?? {}).filter(
      (anchor) =>
        !isAnchorRendered(anchor) &&
        ANCHOR_PATTERN.test(anchor) &&
        document.querySelector(hostSelector(anchor)),
    );
  });

  const discovered = computed(() => {
    void domVersion.value;

    if (!anchorsMode.value) {
      return [];
    }

    const testIds = [...document.querySelectorAll(DISCOVERABLE)]
      .filter(
        (element) =>
          !element.hasAttribute('data-badge-anchor') && !hasRenderedAnchor(element),
      )
      .map((element) => (element as HTMLElement).dataset.testid as string);

    return [...new Set(testIds)].filter(
      (testId) => ANCHOR_PATTERN.test(testId) && !badged.value.includes(testId),
    );
  });

  const anchors = computed(() => [...badged.value, ...discovered.value]);

  // vue drops the teleported badge when a host rewrites its own text: put it back, but not forever
  const HEAL_LIMIT = 3;
  const generation = ref(0);
  let heals = 0;

  watch(domVersion, () => {
    const missing = anchors.value.some((anchor) => {
      const host = document.querySelector(hostSelector(anchor));

      return host && !host.querySelector(`[data-badge-anchor="${anchor}"]`);
    });

    if (!missing) {
      heals = 0;
      return;
    }

    if (heals < HEAL_LIMIT) {
      heals += 1;
      generation.value += 1;
    }
  });
</script>

<template>
  <Teleport
    v-for="anchor in anchors"
    :key="`${anchor}#${generation}`"
    :to="hostSelector(anchor)">
    {{ ' ' }}<VFeatureBadge :anchor="anchor" placed-by-layer />
  </Teleport>
</template>
